import { useCallback, useRef, useState } from 'react';
import { AmbientField } from '../../primitives/AmbientField.tsx';
import { Button } from '../../primitives/Button.tsx';
import { Text } from '../../primitives/Text.tsx';
import { Brand } from '../Brand.tsx';
import styles from './OfflineState.module.css';

/**
 * "Can't reach the server" — filling repository-ui-audit.md § 6.9's gap
 * (there is no page-level empty/error template; every "não foi possível
 * carregar" block is written locally) with an illustrated one rather than a
 * ninth copy of the same two lines of text.
 *
 * The cavaquinho is drawn and playable, not just drawn: § 16 records
 * illustration as an open decision for the system, and a silent instrument
 * would have been the easier, incomplete version of that decision.
 */

interface OfflineStateProps {
  /** What could not be reached, in the learner's own words. */
  title: string;
  /** The line every "unavailable" screen already shares. */
  description?: string;
  onRetry: () => void;
  /**
   * The retry is out. This state stays on screen while it is — replacing it
   * with a loading screen and building it back is a flash, not an answer —
   * and the button reports the wait it started.
   */
  retrying?: boolean;
  /**
   * The two screens that replace the whole application — signed out, or
   * before `AppLayout` mounts at all — carry their own atmosphere and
   * wordmark. Every other caller already sits inside `AppLayout`, which
   * supplies both, so the default leaves them out.
   */
  standalone?: boolean;
}

interface Note {
  readonly name: string;
  readonly freq: number;
}

const NOTES: readonly Note[] = [
  { name: 'Ré', freq: 293.66 },
  { name: 'Sol', freq: 392.0 },
  { name: 'Si', freq: 493.88 },
  { name: 'Ré', freq: 587.33 },
];

const STRING_X = [113, 124, 136, 147] as const;

/** The four plockable strings. Hovering, focusing or clicking one plays it. */
function CavaquinhoStrings() {
  const [hovered, setHovered] = useState(-1);
  const [pulses, setPulses] = useState([0, 0, 0, 0]);
  const audioRef = useRef<AudioContext | null>(null);

  const ensureAudio = useCallback((): AudioContext => {
    if (!audioRef.current) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) throw new Error('Web Audio API unavailable');
      audioRef.current = new Ctor();
    }
    if (audioRef.current.state === 'suspended') void audioRef.current.resume();
    return audioRef.current;
  }, []);

  const playNote = useCallback(
    (index: number) => {
      try {
        const ctx = ensureAudio();
        const freq = NOTES[index]!.freq;
        const now = ctx.currentTime;

        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = freq;

        const overtone = ctx.createOscillator();
        overtone.type = 'sine';
        overtone.frequency.value = freq * 2;

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = freq * 4 + 600;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.32, now + 0.008);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.95);

        const overtoneGain = ctx.createGain();
        overtoneGain.gain.setValueAtTime(0.0001, now);
        overtoneGain.gain.exponentialRampToValueAtTime(0.08, now + 0.006);
        overtoneGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        overtone.connect(overtoneGain);
        overtoneGain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 1);
        overtone.start(now);
        overtone.stop(now + 0.6);
      } catch {
        // Audio unavailable (autoplay policy, no Web Audio support) — the
        // visual pluck below still plays on its own.
      }
    },
    [ensureAudio],
  );

  const pluck = useCallback(
    (index: number) => {
      playNote(index);
      setHovered(index);
      setPulses((current) => current.map((count, i) => (i === index ? count + 1 : count)));
    },
    [playNote],
  );

  const clear = useCallback(() => setHovered(-1), []);

  return (
    <div
      className={styles.instrument}
      role="group"
      aria-label="Cavaquinho ilustrado, com cordas que tocam ao passar o mouse"
    >
      <svg viewBox="0 0 260 460" className={styles.svg} aria-hidden="true">
        <rect x="94" y="14" width="72" height="48" rx="16" className={styles.bodyFill} />
        <line x1="94" y1="28" x2="84" y2="28" className={styles.inkLine} />
        <circle cx="84" cy="28" r="6" className={styles.inkFill} />
        <line x1="94" y1="48" x2="84" y2="48" className={styles.inkLine} />
        <circle cx="84" cy="48" r="6" className={styles.inkFill} />
        <line x1="166" y1="28" x2="176" y2="28" className={styles.inkLine} />
        <circle cx="176" cy="28" r="6" className={styles.inkFill} />
        <line x1="166" y1="48" x2="176" y2="48" className={styles.inkLine} />
        <circle cx="176" cy="48" r="6" className={styles.inkFill} />

        <line x1="106" y1="62" x2="154" y2="62" className={styles.nut} />
        <rect x="105" y="62" width="50" height="155" rx="8" className={styles.bodyFill} />
        <line x1="109" y1="95" x2="151" y2="95" className={styles.fret} />
        <line x1="109" y1="122" x2="151" y2="122" className={styles.fret} />
        <line x1="109" y1="149" x2="151" y2="149" className={styles.fret} />
        <line x1="109" y1="176" x2="151" y2="176" className={styles.fret} />
        <line x1="109" y1="203" x2="151" y2="203" className={styles.fret} />

        <ellipse cx="130" cy="325" rx="92" ry="108" className={styles.bodyFill} />
        <circle cx="130" cy="262" r="32" className={styles.soundholeBg} />
        <circle cx="130" cy="262" r="24" className={styles.soundholeRing} />
        <rect x="110" y="338" width="40" height="10" rx="4" className={styles.inkFill} />

        {STRING_X.map((x, index) => (
          <line
            key={`string-${index}-${pulses[index]}`}
            x1={x}
            y1="62"
            x2={x}
            y2="342"
            strokeWidth="2"
            strokeLinecap="round"
            className={styles.string}
            data-string={index}
            data-active={hovered === index || undefined}
            data-plucking={pulses[index]! > 0 || undefined}
          />
        ))}
      </svg>

      {STRING_X.map((x, index) => (
        <button
          key={index}
          type="button"
          className={styles.stringHit}
          data-string={index}
          aria-label={`Tocar corda ${index + 1}, nota ${NOTES[index]!.name}`}
          onMouseEnter={() => pluck(index)}
          onMouseLeave={clear}
          onFocus={() => pluck(index)}
          onBlur={clear}
          onClick={() => pluck(index)}
        />
      ))}
    </div>
  );
}

export function OfflineState({
  title,
  description = 'Verifique sua conexão e tente novamente.',
  onRetry,
  retrying = false,
  standalone = false,
}: OfflineStateProps) {
  return (
    <div className={styles.root} data-standalone={standalone || undefined}>
      {standalone ? <AmbientField count={2} anchor="viewport" /> : null}
      {standalone ? (
        <span className={styles.wordmark}>
          <Brand />
        </span>
      ) : null}

      <CavaquinhoStrings />

      {/*
        The hint is wrapped rather than styled: `Text` owns its own DOM and
        accepts no `className`, exactly as `Button` does, so the short-screen
        rule that drops this line lives on a transparent span around it.
      */}
      <span className={styles.hint}>
        <Text as="p" variant="body-sm" tone="muted">
          Passe o mouse pelas cordas
        </Text>
      </span>

      <div className={styles.copy}>
        <Text as="h1" variant="heading">
          {title}
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          {description}
        </Text>
        {retrying ? (
          <Button icon="rotate-ccw" disabled disabledReason="Estamos tentando de novo.">
            Tentando novamente…
          </Button>
        ) : (
          <Button icon="rotate-ccw" onClick={onRetry}>
            Tentar novamente
          </Button>
        )}
      </div>
    </div>
  );
}
